import { useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/apiAdmin";
import { HUD } from "./theme";
import { Switch } from "@/components/ui/switch";
import { fmt12 } from "@/lib/time";

const inputCls = `w-full h-10 rounded-xl px-3 text-sm border ${HUD.input}`;
const selectCls = `${inputCls} appearance-none cursor-pointer`;
const pretty = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Panel = ({ title, desc, children }) => (
  <div className={`${HUD.panel} p-5 space-y-4`}>
    <div>
      <h3 className="text-white font-['Outfit'] text-lg">{title}</h3>
      {desc && <p className="text-xs text-gray-400 mt-1">{desc}</p>}
    </div>
    {children}
  </div>
);

const L = ({ label, hint, children }) => (
  <label className="block space-y-1.5">
    <span className={HUD.label}>{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-gray-500">{hint}</span>}
  </label>
);

const okReview = (u) => /^https:\/\/(g\.page|(www\.|maps\.|search\.)?google\.com|maps\.app\.goo\.gl|goo\.gl|g\.co)\//i.test(u || "");

const DELAYS = [[0, "Right away"], [30, "30 minutes"], [60, "1 hour"], [120, "2 hours"], [180, "3 hours"], [360, "6 hours"], [720, "12 hours"], [1440, "24 hours"]];
const HALF_HOURS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const selCls = `h-10 rounded-xl px-3 text-sm border ${HUD.input}`;

const ReviewTiming = ({ f, setF }) => {
  const delay = Number(f.review_text_delay_min ?? 0);
  const preset = DELAYS.some(([m]) => m === delay);
  const [custom, setCustom] = useState(!preset);
  const qs = f.review_quiet_start ?? "20:00";
  const qe = f.review_quiet_end ?? "08:00";
  const quietOn = qs !== qe;
  const on = f.review_text_enabled ?? true;
  return (
    <div className="rounded-xl border border-cyan-400/10 p-3 space-y-3" data-testid="jarvis-review-timing">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-white">Text customers the review link after the job</p>
          <p className="text-xs text-gray-500">The "your detail is complete" email always goes out right away; only the review text waits.</p>
        </div>
        <Switch checked={on} onCheckedChange={(v) => setF({ ...f, review_text_enabled: v })} data-testid="jarvis-review-text-toggle" />
      </div>
      {on && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
            <span>Send</span>
            <select value={custom ? "custom" : delay} onChange={(e) => { const v = e.target.value; if (v === "custom") { setCustom(true); } else { setCustom(false); setF({ ...f, review_text_delay_min: Number(v) }); } }} data-testid="jarvis-review-delay" className={selCls}>
              {DELAYS.map(([m, l]) => <option key={m} value={m}>{l}</option>)}
              <option value="custom">Custom…</option>
            </select>
            {custom && (
              <span className="flex items-center gap-2">
                <input type="number" min="0" step="0.5" value={delay / 60} onChange={(e) => setF({ ...f, review_text_delay_min: Math.round(Number(e.target.value || 0) * 60) })} data-testid="jarvis-review-delay-custom" className={`${selCls} w-24`} />
                <span>hours</span>
              </span>
            )}
            <span>after the job is marked done</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={quietOn} onChange={(e) => setF({ ...f, review_quiet_start: e.target.checked ? "20:00" : "00:00", review_quiet_end: e.target.checked ? "08:00" : "00:00" })} data-testid="jarvis-review-quiet-toggle" />
            <span>Quiet hours: don't text between</span>
            <select value={qs} disabled={!quietOn} onChange={(e) => setF({ ...f, review_quiet_start: e.target.value })} data-testid="jarvis-review-quiet-start" className={`${selCls} disabled:opacity-40`}>
              {HALF_HOURS.map((t) => <option key={t} value={t}>{fmt12(t)}</option>)}
            </select>
            <span>and</span>
            <select value={qe} disabled={!quietOn} onChange={(e) => setF({ ...f, review_quiet_end: e.target.value })} data-testid="jarvis-review-quiet-end" className={`${selCls} disabled:opacity-40`}>
              {HALF_HOURS.map((t) => <option key={t} value={t}>{fmt12(t)}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-gray-500" data-testid="jarvis-review-timing-summary">
            {quietOn ? `If the text would land during quiet hours, JARVIS holds it until ${fmt12(qe)}.` : "Texts send exactly when the delay ends."} Delayed texts go out within 15 minutes of the scheduled time (Arizona time).
          </p>
        </>
      )}
    </div>
  );
};

export const CopyUrl = ({ label, url, testid, help }) => (
  <div className="space-y-1.5">
    <span className={HUD.label}>{label}</span>
    <div className="flex gap-2">
      <code className={`flex-1 rounded-xl px-3 py-2 text-[11px] text-cyan-200 bg-black/50 border border-cyan-400/10 break-all ${HUD.mono}`} data-testid={testid}>{url}</code>
      <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Copied"); }} className={`${HUD.ghost} w-10 h-10 flex items-center justify-center shrink-0`}><Copy className="w-4 h-4" /></button>
    </div>
    {help && <p className="text-[11px] text-gray-500">{help}</p>}
  </div>
);

const Secret = ({ label, field, s, keys, setKeys, unsetHint = "Not connected — stub mode" }) => (
  <L label={label} hint={s[`${field}_set`] ? `Saved (${s[`${field}_hint`]}) — leave blank to keep` : unsetHint}>
    <input type="password" autoComplete="off" value={keys[field] || ""} onChange={(e) => setKeys({ ...keys, [field]: e.target.value })} placeholder={s[`${field}_hint`] || "Paste key"} data-testid={`jarvis-key-${field}`} className={inputCls} />
  </L>
);

export const AISection = ({ f, setF, s }) => (
  <Panel title="AI brain & personality" desc="Pick the model JARVIS thinks with. Claude Opus 5.5 is the most human and intelligent.">
    <L label="AI model">
      <select value={f.ai_model} onChange={(e) => setF({ ...f, ai_model: e.target.value })} data-testid="jarvis-set-ai-model" className={selectCls}>
        {Object.entries(s.ai_models).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
    </L>
    <div className="grid sm:grid-cols-2 gap-3">
      <L label="Personality">
        <select value={f.personality} onChange={(e) => setF({ ...f, personality: e.target.value })} data-testid="jarvis-set-personality" className={selectCls}>
          {Object.entries(s.personalities).map(([k, d]) => <option key={k} value={k}>{pretty(k)} — {d.split(" — ")[0].slice(0, 40)}</option>)}
        </select>
      </L>
      <L label="Reply length">
        <select value={f.reply_length} onChange={(e) => setF({ ...f, reply_length: e.target.value })} data-testid="jarvis-set-reply-length" className={selectCls}>
          {["concise", "balanced", "detailed"].map((k) => <option key={k} value={k}>{pretty(k)}</option>)}
        </select>
      </L>
    </div>
  </Panel>
);

export const VoiceSection = ({ f, setF, s, keys, setKeys }) => {
  const p = s.voice_providers[f.voice_provider] || {};
  return (
    <Panel title="Phone calls provider" desc="Choose who places and answers JARVIS's calls. Only the fields that provider needs are shown.">
      <L label="Provider">
        <select value={f.voice_provider} onChange={(e) => setF({ ...f, voice_provider: e.target.value })} data-testid="jarvis-set-voice-provider" className={selectCls}>
          {Object.entries(s.voice_providers).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </L>
      {!p.live && <p className="text-sm text-amber-300" data-testid="jarvis-voice-coming-soon">This provider is coming soon — JARVIS stays in stub mode (logged, nothing placed) until you pick Vapi or Retell.</p>}
      <label className="flex items-start justify-between gap-4 rounded-xl border border-cyan-400/10 p-3">
        <span><span className="text-sm text-white block">Natural conversation</span>
          <span className="text-xs text-gray-400">JARVIS says "okay", "right", "mm-hmm" while you talk, pauses to think ("hmm, let me check…") before looking things up, and waits a beat so it doesn't cut you off.</span></span>
        <Switch checked={f.voice_human !== false} onCheckedChange={(v) => setF({ ...f, voice_human: v })} data-testid="jarvis-set-voice-human" />
      </label>
      {f.voice_provider === "vapi" && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Secret label="Vapi private API key" field="vapi_api_key" s={s} keys={keys} setKeys={setKeys} />
            <L label="Vapi phone number ID"><input value={f.vapi_phone_number_id} onChange={(e) => setF({ ...f, vapi_phone_number_id: e.target.value })} data-testid="jarvis-set-vapi-phone" className={inputCls} /></L>
          </div>
          <CopyUrl label="Vapi — Server URL (inbound calls)" url={s.vapi_webhook_url} testid="jarvis-vapi-webhook" help="Vapi → Phone Numbers → your number → leave Assistant empty, set Server URL to this. Booking tools are sent automatically." />
        </>
      )}
      {f.voice_provider === "retell" && (
        <>
          <div className="grid sm:grid-cols-3 gap-3">
            <Secret label="Retell API key" field="retell_api_key" s={s} keys={keys} setKeys={setKeys} />
            <L label="Retell phone number"><input value={f.retell_from_number} onChange={(e) => setF({ ...f, retell_from_number: e.target.value })} placeholder="+1 520…" data-testid="jarvis-set-retell-from" className={inputCls} /></L>
            <L label="Retell agent ID"><input value={f.retell_agent_id} onChange={(e) => setF({ ...f, retell_agent_id: e.target.value })} placeholder="agent_…" data-testid="jarvis-set-retell-agent" className={inputCls} /></L>
          </div>
          <p className="text-[11px] text-gray-500">In your Retell agent: set the prompt to <code className="text-cyan-200">{"{{jarvis_prompt}}"}</code> and the begin message to <code className="text-cyan-200">{"{{jarvis_greeting}}"}</code>. Add custom functions <b>list_services</b>, <b>check_availability</b>(date), <b>book_appointment</b>(customer_name, vehicle, services, date, time, address, notes), and for the owner <b>get_bookings</b>(date_from, date_to, search) and <b>run_action</b>(action, params) — all pointing to the Tool URL. Also enable Retell's built-in <b>End Call</b> function so JARVIS can hang up after saying goodbye.</p>
          <CopyUrl label="Retell — Inbound call webhook" url={s.retell_inbound_url} testid="jarvis-retell-inbound" help="Phone Numbers → your number → Inbound webhook URL." />
          <CopyUrl label="Retell — Custom function (tool) URL" url={s.retell_tool_url} testid="jarvis-retell-tool" />
          <CopyUrl label="Retell — Agent webhook (call ended/analyzed)" url={s.retell_webhook_url} testid="jarvis-retell-webhook" />
        </>
      )}
    </Panel>
  );
};

export const SmsSection = ({ f, setF, s, keys, setKeys }) => (
  <Panel title="Text messages (OpenPhone)" desc="Until a key is connected, texts run in stub mode — logged, never faked.">
    <div className="grid sm:grid-cols-2 gap-3">
      <Secret label="OpenPhone API key" field="openphone_api_key" s={s} keys={keys} setKeys={setKeys} />
      <L label="OpenPhone “from” number"><input value={f.openphone_from_number} onChange={(e) => setF({ ...f, openphone_from_number: e.target.value })} placeholder="+1 520 380 4407" data-testid="jarvis-set-op-from" className={inputCls} /></L>
    </div>
    <div className="flex items-center justify-between gap-3 rounded-xl border border-cyan-400/10 p-3">
      <div>
        <p className="text-sm text-white">Auto-reply to customer texts (like a call)</p>
        <p className="text-xs text-gray-500">Team numbers always get full JARVIS by text. Off = customer replies wait in Approvals.</p>
      </div>
      <input type="checkbox" className="w-5 h-5" checked={f.customer_sms_auto_reply !== false} onChange={(e) => setF({ ...f, customer_sms_auto_reply: e.target.checked })} data-testid="jarvis-set-customer-sms-auto" />
    </div>
    <CopyUrl label="OpenPhone — message.received webhook" url={s.openphone_webhook_url} testid="jarvis-op-webhook" help="OpenPhone → Settings → Webhooks → message.received. Team texts get an AI reply; customer texts get an AI draft in Approvals." />
  </Panel>
);

export const BusinessSection = ({ f, setF }) => {
  const b = f.business || {};
  const setB = (k) => (e) => setF({ ...f, business: { ...b, [k]: e.target.value } });
  return (
    <Panel title="Business & invoices" desc="Pre-filled from your website. Used on every invoice and by JARVIS.">
      <div className="grid sm:grid-cols-2 gap-3">
        {[["name", "Business name", "Reflections Auto Detailing"], ["phone", "Phone", "(520) 380-4407"], ["email", "Email", "reflectionsautodetailingaz@gmail.com"], ["website", "Website", "reflectionsautodetail.com"]].map(([k, l, ph]) => (
          <L key={k} label={l}><input value={b[k] || ""} placeholder={ph} onChange={setB(k)} data-testid={`jarvis-biz-${k}`} className={inputCls} /></L>
        ))}
      </div>
      <L label="Google review link (website buttons, invoices, post-service texts/emails, follow-ups)" hint="Paste your link from Google Business Profile → Ask for reviews (https://g.page/r/…/review). Leave blank to use the verified default for your Maricopa profile.">
        <div className="flex gap-2">
          <input value={f.google_review_link || ""} placeholder="https://g.page/r/…/review" onChange={(e) => setF({ ...f, google_review_link: e.target.value })} data-testid="jarvis-set-review-link" className={inputCls} />
          <a href={okReview(f.google_review_link) ? f.google_review_link : undefined} target="_blank" rel="noreferrer" aria-disabled={!okReview(f.google_review_link)} data-testid="jarvis-review-link-test" className={`${HUD.ghost} h-10 px-4 text-xs flex items-center shrink-0 ${okReview(f.google_review_link) ? "" : "opacity-40 pointer-events-none"}`}>Test link</a>
        </div>
      </L>
      <ReviewTiming f={f} setF={setF} />
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input type="checkbox" checked={f.followup_text_customers ?? true} onChange={(e) => setF({ ...f, followup_text_customers: e.target.checked })} data-testid="jarvis-set-followup-toggle" />
        1 month after a completed job, JARVIS texts the customer a check-in / re-book message (you still get a reminder)
      </label>
      <L label="Follow-up text" hint="Placeholders: {first_name} {service} {vehicle} {book_link} {review_link}">
        <textarea rows={3} value={f.followup_message || ""} onChange={(e) => setF({ ...f, followup_message: e.target.value })} data-testid="jarvis-set-followup-message" className={`w-full rounded-xl p-3 text-xs border ${HUD.input}`} />
      </L>
      <L label="Address (shown on invoices)"><input value={b.address || ""} placeholder="Maricopa, AZ · Glendale, AZ" onChange={setB("address")} data-testid="jarvis-biz-address" className={inputCls} /></L>
      <div className="grid sm:grid-cols-2 gap-3">
        <L label="Sales tax %"><input type="number" step="0.01" value={f.tax_rate} onChange={(e) => setF({ ...f, tax_rate: e.target.value })} data-testid="jarvis-set-tax" className={inputCls} /></L>
        <L label="Invoice number prefix"><input value={f.invoice_prefix} onChange={(e) => setF({ ...f, invoice_prefix: e.target.value })} data-testid="jarvis-set-inv-prefix" className={inputCls} /></L>
      </div>
      <L label="Warranty, terms & disclaimers (printed on every invoice)" hint="Leave as is for the default detailing protection terms, or edit.">
        <textarea rows={7} value={f.invoice_terms} onChange={(e) => setF({ ...f, invoice_terms: e.target.value })} data-testid="jarvis-set-terms" className={`w-full rounded-xl p-3 text-xs border ${HUD.input}`} />
      </L>
    </Panel>
  );
};

export const PosSection = ({ f, setF, s }) => {
  const [providers, setProviders] = useState(null);
  useEffect(() => { apiGet("/admin/jarvis/pos-providers").then((d) => setProviders(d.providers)); }, []);
  if (!providers) return null;
  const p = providers[f.pos_provider] || providers.square;
  const creds = f.pos_credentials?.[f.pos_provider] || {};
  const saved = s.pos_credentials_set?.[f.pos_provider] || {};
  const setCred = (k, v) => setF({ ...f, pos_credentials: { ...(f.pos_credentials || {}), [f.pos_provider]: { ...creds, [k]: v } } });
  return (
    <Panel title="Payments / POS" desc="Where deposit links and invoice payments go. Square is connected now; the others store your credentials for activation.">
      <L label="POS provider">
        <select value={f.pos_provider} onChange={(e) => setF({ ...f, pos_provider: e.target.value })} data-testid="jarvis-set-pos" className={selectCls}>
          {Object.entries(providers).map(([k, v]) => <option key={k} value={k}>{v.label}{v.live ? " — connected" : ""}</option>)}
        </select>
      </L>
      {p.live ? (
        <p className="text-sm text-emerald-300" data-testid="jarvis-pos-live">Square is live on this site (deposits & payment links). Credentials are managed securely on the server.</p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            {p.fields.map((k) => (
              <L key={k} label={pretty(k)} hint={saved[k] ? `Saved (${saved[k]})` : ""}>
                <input type="password" autoComplete="off" value={creds[k] || ""} onChange={(e) => setCred(k, e.target.value)} data-testid={`jarvis-pos-${k}`} className={inputCls} />
              </L>
            ))}
          </div>
          <p className="text-[11px] text-amber-300/80">Saved securely. Until activation, deposits keep using Square and every invoice still gets a PDF.</p>
        </>
      )}
    </Panel>
  );
};

export const BankSection = ({ f, setF, s, keys, setKeys }) => {
  const [res, setRes] = useState(null);
  const test = () => { setRes("…"); apiPost("/admin/bank/test", {}).then((d) => setRes(`Connected to Plaid ${d.env}`)).catch((e) => setRes(e?.response?.data?.detail || "Failed")); };
  return (
    <Panel title="Bank (Plaid)" desc="Your Plaid keys from dashboard.plaid.com → Developers → Keys. Values saved here override the deployment settings. Save settings, then Test.">
      <div className="grid sm:grid-cols-2 gap-3">
        <L label="Mode">
          <select value={f.plaid_env || ""} onChange={(e) => setF({ ...f, plaid_env: e.target.value })} data-testid="jarvis-set-plaid-env" className={selectCls}>
            <option value="">Use deployment setting</option>
            <option value="production">Production (real banks)</option>
            <option value="sandbox">Sandbox (test data)</option>
          </select>
        </L>
        <L label="Client ID"><input value={f.plaid_client_id || ""} onChange={(e) => setF({ ...f, plaid_client_id: e.target.value })} placeholder="Leave blank to use deployment setting" data-testid="jarvis-set-plaid-client-id" className={inputCls} /></L>
      </div>
      <Secret label="Secret (must match the mode: Production secret for Production)" field="plaid_secret" s={s} keys={keys} setKeys={setKeys} unsetHint="Not saved here — using the deployment secret" />
      <div className="flex items-center gap-3">
        <button onClick={test} className={`${HUD.ghost} h-9 px-4 text-xs`} data-testid="jarvis-plaid-test">Test Plaid connection</button>
        {res && <span className="text-xs text-gray-300" data-testid="jarvis-plaid-test-result">{res}</span>}
      </div>
    </Panel>
  );
};

const ProcessorRow = ({ name, desc, on, ready, onToggle, testid, children, lockOn }) => (
  <div className={`rounded-xl border p-4 space-y-3 transition-colors ${on ? "border-emerald-400/40 bg-emerald-400/[0.03]" : "border-cyan-400/10"}`} data-testid={testid}>
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-sm text-white flex items-center gap-2">{name}
          <span className={`text-[10px] px-2 rounded-full border ${on ? "border-emerald-400/40 text-emerald-300" : "border-white/10 text-gray-500"}`} data-testid={`${testid}-state`}>{on ? "Active" : ready ? "Off" : "Not set up"}</span>
        </p>
        <p className="text-xs text-gray-500">{desc}</p>
      </div>
      <Switch checked={on} disabled={!on && lockOn} onCheckedChange={onToggle} data-testid={`${testid}-toggle`} />
    </div>
    {children}
  </div>
);

export const PaymentsSection = ({ f, setF, s, keys, setKeys }) => {
  const [res, setRes] = useState(null);
  const active = f.payment_processor || "square";
  const nmiReady = !!(f.nmi_tokenization_key && (s.nmi_security_key_set || keys.nmi_security_key));
  const test = () => { setRes("…"); apiPost("/admin/payments/test", {}).then((d) => setRes(d.message)).catch((e) => setRes(e?.response?.data?.detail || "Failed")); };
  return (
    <Panel title="Payment processors" desc="Deposits, invoices and tips are charged through the ACTIVE processor (only one at a time). Card, Apple Pay and Google Pay. Save settings after switching.">
      <ProcessorRow name="Square" desc={s.square_ready ? "Connected via deployment keys" : "Square keys missing on this deployment"} on={active === "square"} ready={s.square_ready}
        onToggle={(v) => setF({ ...f, payment_processor: v ? "square" : "nmi" })} testid="jarvis-proc-square" />
      <ProcessorRow name="Wholesale Payments (NMI gateway)" desc="Merchant portal → Settings → Security Keys: create a Tokenization key and an API (private) key." on={active === "nmi"} ready={nmiReady} lockOn={!nmiReady}
        onToggle={(v) => setF({ ...f, payment_processor: v ? "nmi" : "square" })} testid="jarvis-proc-nmi">
        <L label="Tokenization key (public)"><input value={f.nmi_tokenization_key || ""} onChange={(e) => setF({ ...f, nmi_tokenization_key: e.target.value })} placeholder="xxxxxx-xxxxxx-xxxxxx-xxxxxx" data-testid="jarvis-set-nmi-tokenization-key" className={inputCls} /></L>
        {!nmiReady && <p className="text-[11px] text-amber-300" data-testid="jarvis-nmi-needs-keys">Enter both keys to turn Wholesale on.</p>}
        <Secret label="Security key (private API key)" field="nmi_security_key" s={s} keys={keys} setKeys={setKeys} unsetHint="Not saved yet" />
        <div className="flex items-center gap-3">
          <button onClick={test} className={`${HUD.ghost} h-9 px-4 text-xs`} data-testid="jarvis-nmi-test">Test Wholesale keys</button>
          {res && <span className="text-xs text-gray-300" data-testid="jarvis-nmi-test-result">{res}</span>}
        </div>
        <p className="text-[11px] text-gray-500">Apple Pay must be enabled in your Wholesale/NMI portal. If Wholesale says you're not on NMI, tell me which gateway they use.</p>
      </ProcessorRow>
    </Panel>
  );
};
